import { legacy_decodeFromBackend } from ".";
import { MIME_TYPES } from "../../packages/excalidraw";
import { decompressData } from "../../packages/excalidraw/data/encode";
import { ImportedDataState } from "../../packages/excalidraw/data/types";
import { FileId } from "../../packages/excalidraw/element/types";
import { t } from "../../packages/excalidraw/i18n";
import {
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "../../packages/excalidraw/types";

const SERVER_URL = import.meta.env.VITE_APP_BACKEND_URL || "/api";
const SCENE_BACKEND_URL = `${SERVER_URL}/scene`;
const ASSETS_BACKEND_URL = `${SERVER_URL}/assets`;

interface CFFunctionsResponse<T> {
  data: T | null;
  error: {
    message: string;
  } | null;
}

export const importFromBackendR2 = async (
  id: string,
  decryptionKey: string,
): Promise<ImportedDataState> => {
  try {
    const response = await fetch(`${SCENE_BACKEND_URL}/${id}`);
    if (response.ok) {
      const buffer = await response.arrayBuffer();
      try {
        const { data: decodedBuffer } = await decompressData(
          new Uint8Array(buffer),
          {
            decryptionKey,
          },
        );
        const data: ImportedDataState = JSON.parse(
          new TextDecoder().decode(decodedBuffer),
        );

        return {
          elements: data.elements || null,
          appState: data.appState || null,
        };
      } catch (error: any) {
        console.warn(
          "error when decoding shareLink data using the new format:",
          error,
        );
        return legacy_decodeFromBackend({ buffer, decryptionKey });
      }
    }
    const json: CFFunctionsResponse<null> = await response.json();
    json.error && window.alert(json.error.message);
    return {};
  } catch (error: any) {
    window.alert(t("alerts.importBackendFailed"));
    console.error(error);
    return {};
  }
};

/**
 * Does this response PROVE the asset reached R2?
 *
 * `response.ok` alone does not: `fetch` follows redirects, so an SSO / captive
 * portal interception answers 200 with someone else's page and the asset is
 * recorded as stored though nothing was written. `/api/assets` answers a
 * successful POST with `{data: {id: <r2 key>}}` (functions/api/assets), and the
 * key ends with the asset id — so a body that doesn't say exactly that is not a
 * write we can vouch for.
 *
 * The content-type is deliberately NOT checked: `responseFormatter` sets no
 * `content-type` header, so the real success response is served as the runtime's
 * default and requiring `application/json` would reject every genuine upload.
 */
const provesStoredAsset = async (response: Response, id: FileId) => {
  try {
    const json: CFFunctionsResponse<{ id: string }> = await response.json();
    return typeof json?.data?.id === "string" && json.data.id.endsWith(id);
  } catch (error: any) {
    return false;
  }
};

export const saveFilesToR2 = async ({
  prefix,
  files,
  token,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
  // Bearer token for owner-gated prefixes (cloud files/<id>/...). The assets
  // endpoint requires it to authorize the write; omitted for legacy keyed
  // prefixes that carry their secret in the URL.
  token?: string;
}) => {
  const erroredFiles = new Map<FileId, true>();
  const savedFiles = new Map<FileId, true>();
  // Why per-file statuses as well as `erroredFiles`: collapsing every failure
  // into a boolean made a PERMANENT rejection (413 — this asset will never fit)
  // indistinguishable from a TRANSIENT one (404/503/offline), so callers could
  // only retry forever. Callers that don't care keep using `erroredFiles`.
  // `0` means `fetch` threw, i.e. no server ever answered.
  const erroredStatuses = new Map<FileId, number>();

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const response = await fetch(`${ASSETS_BACKEND_URL}${prefix}/${id}`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: new Blob([buffer], {
            type: MIME_TYPES.binary,
          }),
        });
        // An unproven 200 is TRANSIENT (its own status, never 413), so the next
        // save uploads the asset again instead of trusting a write that may
        // never have happened.
        if (response.ok && (await provesStoredAsset(response, id))) {
          savedFiles.set(id, true);
        } else {
          erroredFiles.set(id, true);
          erroredStatuses.set(id, response.status);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        erroredStatuses.set(id, 0);
      }
    }),
  );

  return { savedFiles, erroredFiles, erroredStatuses };
};

export const loadFilesFromR2 = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
  // Bearer token for owner-gated prefixes. Reads go THROUGH the worker
  // (`/api/assets`) rather than straight to the bucket URL, so the endpoint's
  // owner/visibility gate is actually on the request path — a private file's
  // asset is not world-readable at a guessable bucket URL. Public files need no
  // token (the gate serves them to anyone); a private file's owner must pass it.
  token?: string,
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const url = `${ASSETS_BACKEND_URL}${prefix}/${id}`;
        const response = await fetch(
          url,
          token ? { headers: { Authorization: `Bearer ${token}` } } : {},
        );
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
